import { PropsWithChildren, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { useCompleteOnboarding } from '@/api/user/mutations/useCompleteOnboarding'
import { useScrollableContainer } from '@/helpers/ScrollToTop'
import { useImpersonatedUserId } from '@/lib/impersonation'
import { RoutePath } from '@/RoutePath'

import { ONBOARDING_DEMO_PKGS } from './demoPkgs'
import { OnboardingTourProvider } from './OnboardingTourContext'
import { ONBOARDING_TOUR_STEPS, type TourPlacement } from './steps'
import { TourCard } from './TourCard'
import { TourSpotlight } from './TourSpotlight'

/** Approximate tour-card height + side offset, used when scrolling mid-page targets into place. */
const TOUR_CARD_RESERVED_PX = 480
const TOUR_CARD_SIDE_OFFSET_PX = 16
const TOUR_CARD_EDGE_PADDING_PX = 16

function getScrollParent(getScrollableContainer: () => { current: HTMLElement | null } | null): HTMLElement | Window {
  return getScrollableContainer()?.current ?? window
}

function scrollByAmount(container: HTMLElement | Window, deltaY: number) {
  // Direct scrollTop — avoids CSS scroll-behavior:smooth and keeps the spotlight from chasing.
  if (container === window) {
    window.scrollTo(0, window.scrollY + deltaY)
  } else {
    ;(container as HTMLElement).scrollTop += deltaY
  }
}

/**
 * Scroll so the target leaves enough room for the tour card on the preferred side.
 * - placement `bottom`: move target toward the top so the card fits below
 * - placement `top`: move target toward the bottom so the card fits above
 */
function scrollToFitTourCard(el: Element, placement: TourPlacement, scrollContainer: HTMLElement | Window) {
  if (placement === 'center') return

  const rect = el.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const needed = TOUR_CARD_RESERVED_PX + TOUR_CARD_SIDE_OFFSET_PX + TOUR_CARD_EDGE_PADDING_PX

  if (placement === 'bottom') {
    // Ideal: target bottom sits just above the reserved card area.
    const idealBottom = viewportHeight - needed
    const delta = rect.bottom - idealBottom
    if (Math.abs(delta) > 8) {
      scrollByAmount(scrollContainer, delta)
    }
    return
  }

  // placement === 'top' — ideal: target top sits just below the reserved card area.
  const idealTop = needed
  const delta = rect.top - idealTop
  if (Math.abs(delta) > 8) {
    scrollByAmount(scrollContainer, delta)
  }
}

function useTargetRect(selector: string | null, enabled: boolean, options: { placement: TourPlacement; scrollToFitCard?: boolean }) {
  const { getScrollableContainer } = useScrollableContainer()
  const [rect, setRect] = useState<DOMRect | null>(null)
  const fittedForSelectorRef = useRef<string | null>(null)
  const settlingRef = useRef(false)
  /** After the first stable measure for a step, ignore scroll chase updates. */
  const frozenForSelectorRef = useRef<string | null>(null)
  const lastRectRef = useRef<{ top: number; left: number; width: number; height: number } | null>(null)
  const settleGenerationRef = useRef(0)
  const getScrollableContainerRef = useRef(getScrollableContainer)
  getScrollableContainerRef.current = getScrollableContainer

  const placement = options.placement
  const scrollToFitCard = Boolean(options.scrollToFitCard)

  const commitRect = useCallback((next: DOMRect) => {
    const prev = lastRectRef.current
    if (
      prev &&
      Math.abs(prev.top - next.top) < 1 &&
      Math.abs(prev.left - next.left) < 1 &&
      Math.abs(prev.width - next.width) < 1 &&
      Math.abs(prev.height - next.height) < 1
    ) {
      return
    }
    lastRectRef.current = {
      top: next.top,
      left: next.left,
      width: next.width,
      height: next.height,
    }
    setRect(next)
  }, [])

  const scrollAndMeasure = useCallback(() => {
    if (!enabled || !selector) {
      lastRectRef.current = null
      setRect(null)
      return
    }
    if (frozenForSelectorRef.current === selector) return

    const el = document.querySelector(selector)
    if (!el) return

    const scrollContainer = getScrollParent(getScrollableContainerRef.current)
    const generation = settleGenerationRef.current

    settlingRef.current = true
    lastRectRef.current = null
    // Full-dim while settling — never chase the target mid-scroll.
    setRect(null)

    try {
      if (scrollToFitCard) {
        if (fittedForSelectorRef.current !== selector) {
          fittedForSelectorRef.current = selector
          scrollToFitTourCard(el, placement, scrollContainer)
        }
      } else {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' })
      }
    } catch {
      // Ignore scroll errors — still measure below.
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (settleGenerationRef.current !== generation) return
        const updated = document.querySelector(selector)
        if (updated) {
          commitRect(updated.getBoundingClientRect())
          frozenForSelectorRef.current = selector
        }
        settlingRef.current = false
      })
    })
  }, [enabled, selector, scrollToFitCard, placement, commitRect])

  const scrollAndMeasureRef = useRef(scrollAndMeasure)
  scrollAndMeasureRef.current = scrollAndMeasure

  // Clear synchronously before paint so the old cutout never flashes on the new step.
  useLayoutEffect(() => {
    settleGenerationRef.current += 1
    fittedForSelectorRef.current = null
    frozenForSelectorRef.current = null
    settlingRef.current = false
    lastRectRef.current = null
    setRect(null)
  }, [selector])

  useEffect(() => {
    if (!enabled) {
      settlingRef.current = false
      frozenForSelectorRef.current = null
      lastRectRef.current = null
      setRect(null)
      return
    }

    // Bind only on selector/enabled — parent re-renders must not cancel pending settle timers.
    const run = () => scrollAndMeasureRef.current()
    const timeoutId = window.setTimeout(run, 80)
    const retryA = window.setTimeout(run, 200)
    const retryB = window.setTimeout(run, 500)

    const onResize = () => {
      if (settlingRef.current) return
      if (selector && frozenForSelectorRef.current === selector) return
      // Allow a fresh measure after viewport resize only.
      frozenForSelectorRef.current = null
      fittedForSelectorRef.current = null
      run()
    }
    window.addEventListener('resize', onResize)

    return () => {
      window.clearTimeout(timeoutId)
      window.clearTimeout(retryA)
      window.clearTimeout(retryB)
      window.removeEventListener('resize', onResize)
    }
  }, [enabled, selector])

  return rect
}

export function OnboardingTour({ children }: PropsWithChildren) {
  const { data: loggedUser, isLoading } = useCurrentUser()
  const impersonatedUserId = useImpersonatedUserId()
  const navigate = useNavigate()
  const location = useLocation()
  const { mutateAsync: completeOnboarding, isPending: isCompleting } = useCompleteOnboarding()

  const shouldShowTour = !isLoading && !!loggedUser && loggedUser.onboardingCompletedAt == null && !impersonatedUserId

  const [stepIndex, setStepIndex] = useState(0)
  const [isDismissed, setIsDismissed] = useState(false)

  const isActive = shouldShowTour && !isDismissed
  const step = ONBOARDING_TOUR_STEPS[stepIndex] ?? ONBOARDING_TOUR_STEPS[0]

  useEffect(() => {
    if (!isActive || stepIndex !== 0) return
    if (!location.pathname.includes(RoutePath.ClientAddresses)) {
      navigate(RoutePath.ClientAddresses, { replace: true })
    }
  }, [isActive, stepIndex, location.pathname, navigate])

  useEffect(() => {
    if (!isActive || !step) return
    if (!location.pathname.includes(step.route)) {
      navigate(step.route)
    }
  }, [isActive, step, location.pathname, navigate])

  const targetRectOptions = useMemo(
    () => ({
      placement: (step?.placement ?? 'bottom') as TourPlacement,
      scrollToFitCard: step?.scrollToFitCard,
    }),
    [step?.placement, step?.scrollToFitCard]
  )

  const targetRect = useTargetRect(step?.targetSelector ?? null, isActive && step?.placement !== 'center', targetRectOptions)

  const demoPkgs = useMemo(() => {
    if (!isActive || !step?.injectDemoPkgs) return null
    return ONBOARDING_DEMO_PKGS
  }, [isActive, step?.injectDemoPkgs])

  const contextValue = useMemo(
    () => ({
      isActive,
      stepIndex,
      demoPkgs,
      forcePkgActionsOpen: Boolean(isActive && step?.forcePkgActionsOpen),
    }),
    [isActive, stepIndex, demoPkgs, step?.forcePkgActionsOpen]
  )

  const handleContinue = useCallback(async () => {
    if (!step) return
    if (step.isFinal) {
      try {
        completeOnboarding()
      } catch {
        // Still dismiss locally so the user isn't stuck if the request fails
      }
      setIsDismissed(true)
      return
    }
    setStepIndex(prev => Math.min(prev + 1, ONBOARDING_TOUR_STEPS.length - 1))
  }, [step, completeOnboarding])

  return (
    <OnboardingTourProvider value={contextValue}>
      {children}
      {isActive && step && (
        <>
          <TourSpotlight stepKey={stepIndex} targetRect={step.placement === 'center' ? null : targetRect} />
          <TourCard
            stepIndex={stepIndex}
            titleKey={step.titleKey}
            bodyKey={step.bodyKey}
            buttonKey={step.buttonKey}
            placement={step.placement}
            targetRect={targetRect}
            Illustration={step.illustration}
            onContinue={handleContinue}
            isContinuing={isCompleting}
          />
        </>
      )}
    </OnboardingTourProvider>
  )
}
